// src/components/AiFeedback.jsx 
import React, { useState, useEffect, useMemo, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeExternalLinks from "rehype-external-links";
import { getFirestore, doc, setDoc, serverTimestamp, getDoc } from "firebase/firestore";

const db = getFirestore();

// --- Helper functions ---
function cleanQuotes(s) {
  if (!s || typeof s !== "string") return s;
  return s.replace(/"(-?\$[\d,]+(?:\.\d{2})?)"/g, '$1').replace(/"(\d+:\d+(?:\.\d+)?)"/g, '$1');
}
function pct(v) { return v == null ? "—" : `${Math.round(v)}%`; }

// Build "observations-only" for a pattern list
function observationsOnlySection(title, items = []) {
  if (!Array.isArray(items) || !items.length) return null;

  const total = items.reduce((acc, it) => acc + (Number(it?.n) || 0), 0) || 0;
  if (total === 0) return null; // No data at all

  const rows = items.map(it => {
    const n  = Number(it?.n) || 0;
    const wr = Number(it?.winRate) || 0;
    const share = total ? Math.round((n / total) * 100) : 0;
    return { key: String(it?.key ?? "—"), wr, n, share };
  }).filter(r => r.n > 0); // Only keep items with trades

  if (!rows.length) return null;

  rows.sort((a, b) => b.n - a.n); // Sort by number of trades (most dominant first)

  const lines = [];
  lines.push(`#### ${title} — Observations`);

  // --- New Categories ---
  const MIN_N_SIGNIFICANT = 8; // Threshold for "statistically relevant"
  const MIN_N_POTENTIAL = 3;   // Threshold for "potential" patterns
  const DOMINANT_SHARE_PCT = 30; // Threshold for "concentration risk"

  const significantWinners = [];
  const significantLaggards = [];
  const potentialWinners = [];
  const potentialLaggards = [];
  const dominantItems = []; // Items with high volume but neutral performance
  const thinDataItems = [];

  // Bucket all items
  for (const r of rows) {
    if (r.n >= MIN_N_SIGNIFICANT) {
      if (r.wr >= 60) {
        significantWinners.push(r);
      } else if (r.wr <= 40) {
        significantLaggards.push(r);
      } else {
        // It's significant in size but neutral in performance.
        if (r.share >= DOMINANT_SHARE_PCT) {
          dominantItems.push(r);
        }
      }
    } else if (r.n >= MIN_N_POTENTIAL) {
      if (r.wr >= 60) {
        potentialWinners.push(r);
      } else if (r.wr <= 40) {
        potentialLaggards.push(r);
      }
    } else {
      thinDataItems.push(r.key);
    }
  }

  // --- Build Narrative ---
  let hasInsights = false;

  if (significantWinners.length) {
    // Sort by win rate (best first)
    significantWinners.sort((a, b) => b.wr - a.wr);
    const list = significantWinners.map(r => `**${r.key}** (${pct(r.wr)}, n=${r.n})`).join(", ");
    lines.push(`- **Strong Performance:** You show a clear, positive edge with: ${list}. This is a statistically relevant pattern; focus on maximizing these opportunities.`);
    hasInsights = true;
  }

  if (significantLaggards.length) {
    // Sort by win rate (worst first)
    significantLaggards.sort((a, b) => a.wr - b.wr);
    const list = significantLaggards.map(r => `**${r.key}** (${pct(r.wr)}, n=${r.n})`).join(", ");
    lines.push(`- **Significant Laggards:** You are consistently underperforming here: ${list}. This pattern is costing you money. **Strongly consider pausing** or fundamentally re-evaluating your approach for these trades.`);
    hasInsights = true;
  }

  if (dominantItems.length) {
    // Sort by share (most dominant first)
    dominantItems.sort((a, b) => b.share - a.share);
    const list = dominantItems.map(r => `**${r.key}** (${r.share}% of trades, n=${r.n}, ${pct(r.wr)} WR)`).join(", ");
    lines.push(`- **Concentration Risk:** A large portion of your trades fall here: ${list}. Your performance is neutral (${pct(dominantItems[0].wr)} WR), suggesting a lot of "churn" without a clear edge. Is the effort here worth the return?`);
    hasInsights = true;
  }

  if (potentialWinners.length) {
    // Sort by win rate (best first)
    potentialWinners.sort((a, b) => b.wr - a.wr);
    const list = potentialWinners.map(r => `**${r.key}** (${pct(r.wr)}, n=${r.n})`).join(", ");
    lines.push(`- **Promising Patterns:** Early data suggests you may have an edge here: ${list}. The sample size (n<${MIN_N_SIGNIFICANT}) is small, but the results are positive. Track with small size to validate.`);
    hasInsights = true;
  }

  if (potentialLaggards.length) {
    // Sort by win rate (worst first)
    potentialLaggards.sort((a, b) => a.wr - b.wr);
    const list = potentialLaggards.map(r => `**${r.key}** (${pct(r.wr)}, n=${r.n})`).join(", ");
    lines.push(`- **Concerning Patterns:** Be cautious with: ${list}. While the sample size is small, the initial results are poor. This could be an early warning sign of a weak spot.`);
    hasInsights = true;
  }

  if (thinDataItems.length) {
    const keys = thinDataItems.join(", ");
    lines.push(`- **Insufficient Data:** Items like ${keys} have too few trades (n<${MIN_N_POTENTIAL}) to draw any conclusions. Treat as purely exploratory.`);
    hasInsights = true;
  }

  if (!hasInsights && rows.length > 0) {
    // Fallback if no specific buckets were hit but we have data
    const dominant = rows[0];
    if (dominant) {
       lines.push(`- Your most frequent exposure is **${dominant.key}** (${dominant.n} trades, ${pct(dominant.wr)} WR), making up ${dominant.share}% of this category. There isn't a strong win or loss pattern here yet.`);
    } else {
      return null; // Should be impossible if rows.length > 0, but a good guard.
    }
  } else if (!hasInsights && rows.length === 0) {
      return null; // No data to show
  }

  lines.push(""); // spacer
  return lines.join("\n");
}

// Build rock-solid Markdown from the structured JSON
function markdownFromJson(ai) {
  if (!ai || typeof ai !== "object") return "";

  const out = [];

  // 1. The New Narrative Section (Coach's Insight) - Placed at the top
  if (ai.narrative) {
    out.push("### 🧠 Coach's Insight");
    // Format as a blockquote
    out.push(ai.narrative.split('\n').map(p => `> ${p}`).join('\n'));
    out.push("\n---"); // Separator
  }

  if (Array.isArray(ai.top_trends) && ai.top_trends.length) {
    out.push("### 📈 Top Trends Analysis");
    ai.top_trends.slice(0, 3).forEach((t, i) => {
      // Formatting: Bold the label, then normal text for the explanation
      const label = t?.label ? `**${t.label}**` : "—";
      const ev = t?.evidence ? `: ${t.evidence}` : "";
      out.push(`${i + 1}. ${label}${ev}`);
    });
  }

  if (Array.isArray(ai.strengths) && ai.strengths.length) {
    out.push("\n### 💪 Strengths");
    ai.strengths.forEach(s => out.push(`- ${s}`));
  }

  if (Array.isArray(ai.weaknesses) && ai.weaknesses.length) {
    out.push("\n### ⚠️ Weaknesses & Leaks");
    ai.weaknesses.forEach(w => out.push(`- ${w}`));
  }

  if (Array.isArray(ai.risk_flags) && ai.risk_flags.length) {
    out.push("\n### 🚩 Critical Risk Flags");
    ai.risk_flags.forEach(rf => {
      const sev = rf?.severity ? ` (**${rf.severity.toUpperCase()}**)` : "";
      const label = rf?.label ? `**${rf.label}**` : "—";
      const ev = rf?.evidence ? ` — ${rf.evidence}` : "";
      out.push(`- ${label}${sev}${ev}`);
    });
  }
  
  const p = ai.patterns || {};
  const patternLists = [
    ["day_of_week", "Day of Week"],
    ["session", "Session"],
    ["time_of_day", "Time of Day"],
    ["instrument", "Instrument"],
    ["mood_correlation", "Mood Correlation"],
  ];
  if (patternLists.some(([k]) => Array.isArray(p[k]) && p[k].length)) {
    out.push("\n### 🔍 Pattern Discovery — Observations");
    patternLists.forEach(([k, title]) => {
      const section = observationsOnlySection(title, p[k]); 
      if (section) out.push(section);
    });
  }

  if (Array.isArray(ai.suggestions) && ai.suggestions.length) {
    out.push("\n### 🚀 Action Plan");
    ai.suggestions.forEach(s => out.push(`- ${s}`));
  }

  return out.join("\n");
}

export default function AiFeedbackModule({
  stats = {},
  filteredRows = [],
  userId = "anon",
  accountId = "default",
  activeFilters = {},
  customTitle = "AI Trading Analysis",
  hideLocalInsights = false,
  autoGenerate = false, // Default is FALSE (Manual Mode)
  hideControls = false  // NEW PROP: Hides buttons/header
}) {

  const [includeNotes, setIncludeNotes] = useState(true);

  // Scope summary shown under the title
  const scopeSummary = useMemo(
    () => ({ tradeCount: filteredRows.length, filters: activeFilters || null }),
    [filteredRows.length, activeFilters]
  );

  // Build a per-filter localStorage key (stable with your current scheme)
  const keyFromFilters = (f) => {
    if (!f) return "nofilters";
    const compact = [
      `date=${f.dateFilter || "all"}`,
      `from=${f.customFrom || ""}`,
      `to=${f.customTo || ""}`,
      `prod=${f.productFilter || ""}`,
      `res=${f.resultFilter || ""}`,
      `rules=${f.rulesFilter || ""}`,
      `pos=${f.positionFilter || ""}`,
      `es=${f.entryStrategyFilter || ""}`,
      `xs=${f.exitStrategyFilter || ""}`,
    ].join("|");
    return compact;
  };

  const localKey = useMemo(
    () => `trapound-aiFeedback:${userId}:${accountId}:${includeNotes ? 1 : 0}:${keyFromFilters(activeFilters)}`,
    [userId, accountId, includeNotes, activeFilters]
  );

  const [loading, setLoading] = useState(false);
  const [aiFeedback, setAiFeedback] = useState("");
  const [error, setError] = useState("");
  const prevFeedbackRef = useRef("");
  const [hasSavedForThisAccount, setHasSavedForThisAccount] = useState(false);

  useEffect(() => {
      try {
       const saved = localStorage.getItem(localKey);
       setHasSavedForThisAccount(!!saved);
       setAiFeedback(saved || "");
      } catch {
       setHasSavedForThisAccount(false);
       setAiFeedback("");
      }
    }, [localKey]);

  useEffect(() => {
    // 1. Must be enabled (autoGenerate=true)
    // 2. Must not be already loading or have feedback
    // 3. Must have data to analyze
    if (autoGenerate && !loading && !aiFeedback && !error && filteredRows.length > 0) {
       handleGetFeedback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGenerate, filteredRows.length]);

  useEffect(() => {
    let cancelled = false;
    if (aiFeedback) return; // already have content for this account/filters
    (async () => {
      try {
        const ref = doc(db, "users", userId, "accounts", accountId, "ai_feedback", "latest");
        const snap = await getDoc(ref);
        if (!cancelled && snap.exists()) {
          const data = snap.data();
          const filtersMatch =
            JSON.stringify(data?.filters || {}) === JSON.stringify(activeFilters || {});
          if (filtersMatch && typeof data.summaryText === "string" && data.summaryText.trim()) {
            setAiFeedback(data.summaryText);
            localStorage.setItem(localKey, data.summaryText);
            setHasSavedForThisAccount(true);
          }
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [userId, accountId, activeFilters, localKey, aiFeedback]);

  // ---------- Helpers ----------
  const parseNum = (val) => {
    if (val == null) return null;
    if (typeof val === "number") return val;
    const n = Number(String(val).replace(/[^0-9.-]/g, ""));
    return Number.isFinite(n) ? n : null;
  };
  const shortProduct = (v) => (v ? String(v).split(" - ")[0] : "") || null;

  const parseRiskToReward = (v) => {
    if (v == null) return null;
    const s = String(v).trim();
    // "a:b" → b/a
    const mColon = s.match(/^\s*([0-9]*\.?[0-9]+)\s*:\s*([0-9]*\.?[0-9]+)\s*$/);
    if (mColon) {
      const risk = parseFloat(mColon[1]);
      const reward = parseFloat(mColon[2]);
      if (risk > 0 && isFinite(reward)) return reward / risk;
      return null;
    }
    // Single numeric ratio like "2", "1.5R"
    const mSingle = s.match(/([0-9]*\.?[0-9]+)/);
    if (mSingle) {
      const num = parseFloat(mSingle[1]);
      return isFinite(num) ? num : null;
    }
    return null;
  };

  // Ensure $ amounts look like $1,234.56 without touching non-$ tokens
  function normalizeUSD(markdown) {
    return markdown.replace(
      /\$(\-?\d{1,3}(?:,\d{3})*|\-?\d+)(\.\d+)?/g,
      (_, intPart, dec) => {
        const n = Number((intPart || "0").replace(/,/g, "") + (dec || ""));
        if (!Number.isFinite(n)) return `$${intPart}${dec || ""}`;
        return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
      }
    );
  }

  // ---------- Build journals from filtered rows (exactly what user sees) ----------
  const MAX_ROWS = 400; // safety cap
  const journals = useMemo(() => {
    // 1. DATA LIMIT LOGIC:
    // If autoGenerate is TRUE (End of Trial), send EVERYTHING (filteredRows).
    // If autoGenerate is FALSE (Stats Page), limit to last 400 to save tokens/speed.
    const src = Array.isArray(filteredRows) 
      ? (autoGenerate ? filteredRows : filteredRows.slice(-MAX_ROWS)) 
      : [];

    // 2. NOTES LOGIC:
    // If autoGenerate is TRUE, force notes ON so AI has full context.
    // Otherwise, respect the user's checkbox.
    const shouldIncludeNotes = autoGenerate ? true : includeNotes;

    const base = (r) => ({
      date: r["Date"] ?? null,
      product: shortProduct(r["Product"]),
      session: r["Session"] ?? null,
      win: r["Win"] === true || r["Win"] === "TRUE" || r["Win"] === "Yes",
      loss: r["Loss"] === true || r["Loss"] === "TRUE" || r["Loss"] === "Yes",
      reward: parseNum(r["Actual Reward"]),
      rrr: parseRiskToReward(r["Actual Risk to Reward"] ?? r["Risk/Reward"]),
      ticks: parseNum(r["Ticks"]),
      duration: r["Duration (h:mm)"] ?? r["Duration"] ?? null,
      entryTime: r["Entry Time"] ?? null,
      exitTime: r["Exit Time"] ?? null,
      entryStrategy: r["Entry Strategy"] ?? null,
      exitStrategy: r["Exit Strategy"] ?? null,
      mood: r["Mood/Mindset"] ?? null,
    });

    if (!shouldIncludeNotes) return src.map((r) => ({ ...base(r), notes: "" }));

    return src.map((r) => ({
      ...base(r),
      notes: [
        r["Notes"] ?? "",
        r["Lesson"] ? `Lesson: ${r["Lesson"]}` : "",
        r["Emotional Notes"] ? `Emotional Notes: ${r["Emotional Notes"]}` : ""
      ]
        .filter(Boolean)
        .join("\n")
        .trim(),
    }));
  }, [filteredRows, includeNotes, autoGenerate]);

  // ---------- Actions ----------
  const handleGetFeedback = async () => {
    if (!filteredRows || !filteredRows.length) {
      setError("No trades match the current filters.");
      return;
    }
    prevFeedbackRef.current = aiFeedback;
    setAiFeedback("");
    setLoading(true);
    setError("");

    // FIX: Use the Create React App environment variable
    const API_URL = process.env.REACT_APP_API_URL || "https://trapoundapi.onrender.com";

    try {
      const payload = {
        stats,     // ← now accepts the expanded bundle (streaks, moods, durations, timeframeAgg, etc.)
        journals,  // ← compact journals mirrored from current filter scope
        scope: {
          userId,
          accountId,
          filteredCount: filteredRows.length,
          activeFilters: activeFilters || null,
          version: "2025-09-11-expanded-v2"
        }
      };
      
      const resp = await fetch(`${API_URL}/analyzeStats`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      // Try to parse the JSON *before* checking if the response was OK
      const data = await resp.json().catch(() => null);

      if (!resp.ok) {
        // If the server sent a specific error message, throw THAT instead
        const errorMsg = data?.error?.message || data?.error || `Server Error: ${resp.status}`;
        throw new Error(errorMsg);
      }

      if (data && data.error) throw new Error(data.error.message || "Error from API");

      // --- UPDATED LOGIC ---
      let md = "";
      const rawFeedback = data.aiFeedback || "";
      const feedbackJson = data.aiFeedbackJson;

      if (feedbackJson) {
        // Case 1: Success, JSON was parsed on server
        md = markdownFromJson(feedbackJson);
      } else if (rawFeedback && rawFeedback.trim().startsWith("{")) {
        // Case 2: Failure, server sent back a string that *looks* like
        // truncated JSON. This is an error.
        console.error("Failed to parse AI JSON response:", rawFeedback);
        throw new Error("Failed to parse AI response. The data may have been truncated by the server. Please try again.");
      } else {
        // Case 3: Legacy/Fallback, server sent back plain text (not JSON)
        md = cleanQuotes(rawFeedback);
      }
      // --- END UPDATED LOGIC ---

      const feedback = normalizeUSD(md);
      setAiFeedback(feedback);

      if (feedback) {
        localStorage.setItem(localKey, feedback);
        const ref = doc(db, "users", userId, "accounts", accountId, "ai_feedback", "latest");
        await setDoc(ref, {
          summaryText: feedback,
          filteredCount: filteredRows.length,
          filters: activeFilters || null,
          updatedAt: serverTimestamp(),
        }, { merge: true });
        setHasSavedForThisAccount(true);
      } 
    } catch (e) {
      setError(e.message || "Network error or API unavailable.");
      setAiFeedback(prevFeedbackRef.current || "");
    } finally {
      setLoading(false);
    }
  };

  const handleClearFeedback = () => {
    setAiFeedback("");
    localStorage.removeItem(localKey);
    setHasSavedForThisAccount(false);
  };

  // ---------- Local Quick Insights (fallback if no API text) ----------
  const quick = useMemo(() => {
    const s = stats || {};
    const pct = (v) =>
      `${Number(v || 0).toFixed(2).replace(/\.00$/, "")}%`;
    const fmtUsd = (v) =>
      new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 })
        .format(Number(v || 0));

    const rr =
      s.averageRiskToReward && isFinite(s.averageRiskToReward) && s.averageRiskToReward > 0
        ? `1:${parseFloat(s.averageRiskToReward.toFixed(2)).toString()}`
        : "--";

    return [
      s.winPct != null ? `Win rate: **${s.winPct}%** (${s.tradeCount ?? 0} trades).` : null,
      s.overallIncrease != null && s.overallPct != null
        ? `Overall P&L: **${fmtUsd(s.overallIncrease)}** (${pct(s.overallPct)}).`
        : null,
      s.todayInc != null && s.todayPct != null
        ? `Today: **${fmtUsd(s.todayInc)}** across ${s.todayCount ?? 0} trades (${pct(s.todayPct)}).`
        : null,
      s.weekInc != null && s.weekPct != null
        ? `This week: **${fmtUsd(s.weekInc)}** across ${s.weekCount ?? 0} trades (${pct(s.weekPct)}).`
        : null,
      s.bestProduct ? `Best product: **${s.bestProduct}**.` : null,
      s.worstProduct ? `Worst product: **${s.worstProduct}**.` : null,
      s.buyCount ? `Buys win rate: **${Math.round(((s.buyWinCount || 0)/(s.buyCount || 1))*100)}%**.` : null,
      s.sellCount ? `Sells win rate: **${Math.round(((s.sellWinCount || 0)/(s.sellCount || 1))*100)}%**.` : null,
      rr !== "--" ? `Average risk:reward: **${rr}**.` : null,
      s.quickSuccessionTrades != null ? `Quick-succession trades (≤2m apart): **${s.quickSuccessionTrades}**.` : null,
      s.bestDay ? `Best day of week: **${s.bestDay}**.` : null,
      s.worstDay ? `Worst day of week: **${s.worstDay}**.` : null,
      s.bestDayPct != null ? `Best day % increase: **${pct(s.bestDayPct)}**${s.bestDayPctDate ? ` on **${new Date(s.bestDayPctDate).toLocaleDateString()}**` : ""}.` : null,
      s.averageWinTrade != null ? `Average winning trade: **${fmtUsd(s.averageWinTrade)}**.` : null,
      s.averageLosingTrade != null ? `Average losing trade: **${fmtUsd(s.averageLosingTrade)}**.` : null,
      s.winRateWhenRules != null ? `Win rate when following rules: **${s.winRateWhenRules}%** (Loss rate: **${s.lossWhenRules ?? (100 - s.winRateWhenRules)}%**).` : null,
      s.moodTradeCount ? `Mood sample size: **${s.moodTradeCount}**.` : null,
      (s.winRatePositive != null || s.winRateNeutral != null || s.winRateNegative != null)
        ? `Win rate by mood — Positive: **${s.winRatePositive ?? 0}%**, Neutral: **${s.winRateNeutral ?? 0}%**, Negative: **${s.winRateNegative ?? 0}%**.`
        : null,
    ].filter(Boolean);
  }, [stats]);

  // ---------- Render ----------
  return (
    <div
      className={hideControls ? "" : "bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mt-10 mb-12"}
      data-ignore-pdf="1"
    >
      {/* Conditionally render Header & Controls */}
      {!hideControls && (
        <>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-blue-900 dark:text-blue-200">{customTitle}</div>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Analysis reflects your current filters (scope: {scopeSummary.tradeCount} trade{scopeSummary.tradeCount === 1 ? "" : "s"}).
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                className="w-4 h-4"
                checked={includeNotes}
                onChange={(e) => setIncludeNotes(e.target.checked)}
              />
              Include journal notes
            </label>
          </div>

          <div className="flex gap-4 mt-4">
            <button
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-bold transition disabled:opacity-60"
              onClick={handleGetFeedback}
              disabled={loading}
            >
              {loading ? "Analyzing..." : "Generate AI Feedback"}
            </button>
            {aiFeedback && (
              <button
                className="px-5 py-2 bg-gray-300 dark:bg-gray-700 text-gray-800 dark:text-white rounded-2xl font-bold transition"
                onClick={handleClearFeedback}
              >
                Clear Feedback
              </button>
            )}
          </div>
        </>
      )}

      {/* If controls are hidden, we still need to show loading status nicely */}
      {hideControls && !aiFeedback && (
         <div className="text-center py-8 text-blue-600 dark:text-blue-400 animate-pulse font-semibold">
            Generating your End of Trial Analysis...
         </div>
      )}

      {error && <div className="mt-5 text-red-600 font-semibold">{error}</div>}

      {/* API-rendered Markdown feedback */}
      {aiFeedback && (
        <div className={`${hideControls ? "mt-0" : "mt-8"} p-6 rounded-2xl shadow-xl bg-gradient-to-br from-blue-50 via-white to-blue-100 dark:from-gray-900 dark:via-gray-800 dark:to-blue-950 border-l-8 border-blue-500 dark:border-blue-400 max-w-5xl mx-auto`}>
          <div className="flex items-center gap-3 mb-3">
            <span className="bg-blue-600 text-white rounded-full p-2 shadow">
              <svg width="24" height="24" fill="none">
                <path d="M12 2v20M2 12h20" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </span>
            <h3 className="text-xl font-bold text-blue-900 dark:text-blue-200 tracking-tight">
              {customTitle}
            </h3>
          </div>
          <div className="prose prose-blue dark:prose-invert max-w-none text-base leading-relaxed font-medium">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[
                rehypeRaw,
                [rehypeExternalLinks, { target: "_blank", rel: ["noopener", "noreferrer"] }]
              ]}
            >
              {aiFeedback}
            </ReactMarkdown>
          </div>
        </div>
      )}

      {/* Local quick insights (shown only when API content is empty & not loading) */}
      {!loading && !aiFeedback && !hideLocalInsights && hasSavedForThisAccount && quick.length > 0 && (
        <div className="mt-8 p-6 rounded-2xl border border-blue-200 dark:border-gray-700 bg-blue-50/60 dark:bg-gray-900/60 max-w-5xl mx-auto">
          <h4 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-2">Quick Insights</h4>
          <ul className="list-disc pl-5 text-sm text-gray-700 dark:text-gray-200 space-y-1">
            {quick.map((line, i) => (
              <li key={i} dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") }} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}